/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TYPE_DESC_TYPES } from "@adapptio/lib-blueprint";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { CodeEditor } from "@adapptio/lib-editor-ui";
import { capitalizeFirstLetter } from "@adapptio/lib-shared";
import React, { useEffect, useState } from "react";
import { TReferenceData } from "../../ReferenceGuideTypes";
import "./FunctionsPreview.scss";

export interface IFunctionsPreviewProps {
	className?: string;
	data: TReferenceData;
}

const generateExample = (data, t) => {
	let returnType;

	const baseTypes = [
		TYPE_DESC_TYPES.STRING,
		TYPE_DESC_TYPES.INTEGER,
		TYPE_DESC_TYPES.BOOLEAN,
		TYPE_DESC_TYPES.VOID,
		TYPE_DESC_TYPES.ANY,
		TYPE_DESC_TYPES.FLOAT,
		TYPE_DESC_TYPES.NULL
	];

	if (baseTypes.indexOf(data.returnType?.type) >= 0) {
		returnType = ": " + data.returnType.type;
	} else if (data.returnType?.type === "array") {
		if (data.returnType?.items) {
			returnType = `: ${data.returnType.items[0].type}[]`;
		} else {
			returnType = ": Array";
		}
	} else {
		returnType = "";
	}

	returnType = capitalizeFirstLetter(returnType);

	const nameFunc = data.name;
	const args = data.child.map((item, idx) => {
		if (item) {
			const translateName = t("editor", item.label);
			const formatName = translateName ? translateName.toLowerCase().split(" ").join("_") : 'argument';

			const argName = formatName;
			const argRequire = !item.required ? "?" : "";
			const argType = item.type || item.getTypeDescriptor().type;

			const spacer = idx > 0 ? " " : "";

			return `${spacer}${argName}${argRequire}: ${argType}`;
		}
	});

	const argsRest = data.argsRest?.type ? `${args.length ? ", " : ""}...args` : "";
	const argsRestType = data.argsRest?.type ? `: ${data.argsRest.type}` : "";

	const result = `${nameFunc}(${args}${argsRest}${argsRestType})${returnType}`;

	return result;
};

export const FunctionsPreview: React.FC<IFunctionsPreviewProps> = ({ className, data }) => {
	const t = useTranslate();

	const baseClass = BEM("edt-functions-preview");

	const [ dataStructure, setDataStructure ] = useState("");

	if (className) {
		baseClass.add(className);
	}

	useEffect(() => {
		setDataStructure(generateExample(data, t));
	}, [ data ]);

	if (dataStructure === null) {
		return null;
	}

	return (
		<div className={baseClass.toClassName()}>
			<CodeEditor
				value={dataStructure}
				language="adapptioExpression"
				readOnly={true}
				options={{
					minimap: { enabled: false },
					scrollBeyondLastLine: false,
					scrollBeyondLastColumn: 0,
					automaticLayout: true,
					tabSize: 2,
					lineNumbers: "off",
					lineDecorationsWidth: 0,
					readOnly: true,
					renderLineHighlightOnlyWhenFocus: true
				}}
			/>
		</div>
	);
};
